import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { MaskLines } from "@/components/motion";
import { waLink } from "@/lib/site";
import { useLang } from "@/context/LangContext";

const Stage = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 80, damping: 18 });
  const sy = useSpring(my, { stiffness: 80, damping: 18 });
  const rotY = useTransform(sx, [-0.5, 0.5], [-7, 7]);
  const rotX = useTransform(sy, [-0.5, 0.5], [6, -6]);
  const fx = useTransform(sx, [-0.5, 0.5], [-26, 26]);
  const fy = useTransform(sy, [-0.5, 0.5], [-18, 18]);
  const bx = useTransform(sx, [-0.5, 0.5], [12, -12]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div onMouseMove={onMove} onMouseLeave={reset} className="relative mx-auto h-[440px] w-full max-w-[520px] sm:h-[560px] lg:h-[640px] [perspective:1200px]" data-testid="hero-stage">
      <motion.div style={{ rotateX: rotX, rotateY: rotY }} className="relative h-full w-full [transform-style:preserve-3d]">
        <motion.div style={{ x: bx }} initial={{ clipPath: "inset(100% 0 0 0 round 999px 999px 24px 24px)" }} animate={{ clipPath: "inset(0% 0 0 0 round 999px 999px 24px 24px)" }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.25 }}
          className="absolute inset-x-[6%] bottom-0 top-[10%] overflow-hidden arch bg-[var(--leaf)]">
          <img src="/assets/img/hero-leaves.jpg" alt="" className="h-full w-full object-cover opacity-35 mix-blend-luminosity" />
          <div className="absolute inset-0 bg-gradient-to-t from-leafdeep/70 to-transparent" />
        </motion.div>
        <motion.img src="/assets/img/owner.webp" alt="Ade Wardhana Adinata S.E., M.M — Owner Minaqu Indonesia" data-testid="hero-owner-photo"
          initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.7 }}
          style={{ translateZ: 60 }}
          className="absolute bottom-0 left-[8%] h-[96%] w-auto max-w-[92%] object-contain object-bottom drop-shadow-[0_30px_40px_rgba(7,72,31,0.35)]" />
        <motion.img src="/assets/p/ladder-plant-tanaman-turus-tangga.webp" alt="Ladder Plant" style={{ x: fx, y: fy }}
          initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.1, duration: 0.9 }}
          className="absolute -left-2 bottom-4 h-40 sm:h-56 lg:h-64 w-auto floaty" />
        <motion.img src="/assets/img/marigold.webp" alt="Bunga Marigold" style={{ x: fy, y: fx }}
          initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.25, duration: 0.9 }}
          className="absolute -right-2 top-[18%] h-24 sm:h-32 w-auto floaty [animation-delay:1.5s]" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 0.8 }}
          style={{ translateZ: 100 }} className="absolute bottom-5 right-0 z-10 sm:right-2 rounded-2xl bg-white/95 px-4 py-3 shadow-xl backdrop-blur" data-testid="hero-owner-card">
          <p className="font-display text-[13px] sm:text-sm font-extrabold text-[var(--leaf-deep)]">Ade Wardhana Adinata S.E., M.M</p>
          <p className="text-xs text-[var(--muted-ink)]">Owner · Digital-Savvy Entrepreneur Hero</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const wy = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const { t, lang } = useLang();
  return (
    <section ref={ref} data-testid="home-hero" className="relative overflow-hidden bg-[var(--paper)] pt-24 sm:pt-28">
      <motion.p style={{ y: wy }} aria-hidden className="pointer-events-none absolute -left-4 top-24 select-none font-display text-[34vw] font-black leading-none tracking-tighter text-[rgba(15,122,53,0.05)]">minaqu</motion.p>
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-8 px-5 sm:px-8 lg:grid-cols-12 lg:gap-4">
        <div className="lg:col-span-7 pt-6 lg:pt-0">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="eyebrow">Indonesia Trusted Ornamental Plant Supplier · Est. 2019</motion.p>
          <MaskLines key={lang} testid="home-hero-title" lines={[t("Tanaman hias", "Indonesian"), t("asli Indonesia,", "native plants,"), <span key="u" className="text-[var(--ink)]">{t("untuk dunia.", "for the world.")}</span>]}
            className="mt-6 font-display text-[3rem] leading-[0.92] sm:text-7xl lg:text-[4.6rem] xl:text-[5.4rem] font-black tracking-[-0.03em] text-[var(--leaf)]" />
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8 }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[var(--muted-ink)]">
            {t("Minaqu Indonesia memproduksi dan memasok tanaman eksotis asli Indonesia — bare root, tanaman pot, kultur jaringan, hingga souvenir — bersama lebih dari 1.000 petani mitra dari Aceh sampai Papua.", "Minaqu Indonesia produces and supplies exotic plants native to Indonesia — bare root, potted plants, tissue culture and souvenirs — together with more than 1,000 partner farmers from Aceh to Papua.")}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="mt-9 flex flex-wrap gap-3">
            <Link to="/katalog-tanaman" data-testid="hero-catalog-cta" className="group inline-flex items-center gap-2 rounded-full bg-[var(--leaf)] px-6 py-3.5 font-display text-sm font-bold text-white transition-transform hover:-translate-y-0.5">
              {t("Lihat Katalog Tanaman", "View Plants Catalogue")} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
            </Link>
            <a href={waLink()} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-cta" className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--leaf)] px-6 py-3 font-display text-sm font-bold text-[var(--leaf)] transition-colors hover:bg-[var(--leaf)] hover:text-white">
              <MessageCircle className="h-4 w-4" /> Order by WhatsApp
            </a>
          </motion.div>
        </div>
        <div className="lg:col-span-5"><Stage /></div>
      </div>
    </section>
  );
};
